import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updateProfile,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '@/lib/firebase';
import { api } from '@/services/api';
import { consumePendingLegalAcceptance } from '@/lib/legalAcceptance';
import { pickInitialCompany } from '@/lib/selectedCompany';
import { setSentryTag, setSentryUser } from '@/lib/sentryInit';
import type { User, Company } from '@/types';

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  selectedCompany: Company | null;
  /**
   * Which Firebase auth providers are linked to this account.
   * Drives Profile → Security sign-in method badges and password UX.
   */
  authProviders: { google: boolean; password: boolean };
  /**
   * `true` when Google is the only linked provider (no HiroMatch password yet).
   */
  isGoogleOnly: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  setSelectedCompany: (company: Company) => void;
  /** Re-fetch backend profile (e.g. after PUT /users/me). */
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};

const API_BASE = 'https://europe-west3-recruiterai-8d65b.cloudfunctions.net/api/v1';

async function syncBackendSession(firebaseUser: FirebaseUser): Promise<User> {
  const token = await firebaseUser.getIdToken(true);

  const pendingLegal = consumePendingLegalAcceptance();
  const sessionRes = await fetch(`${API_BASE}/auth/session`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(
      pendingLegal
        ? {
            legalAcceptance: {
              termsVersion: pendingLegal.termsVersion,
              onerousClausesVersion: pendingLegal.onerousClausesVersion,
              professionalDeclaration: true as const,
            },
          }
        : {},
    ),
  });

  if (!sessionRes.ok) {
    const errText = await sessionRes.text();
    throw new Error(`POST /auth/session failed: ${sessionRes.status} — ${errText}`);
  }

  const sessionJson = (await sessionRes.json()) as {
    timeZone?: string;
    displayName?: string;
    preferredLanguage?: string;
    [k: string]: unknown;
  };

  // memberships map: keyed by companyId, from GET /users/me/companies
  const memberships: Record<string, import('@/types').UserMembership> = {};
  try {
    const companiesRes = await fetch(`${API_BASE}/users/me/companies`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (companiesRes.ok) {
      const json = await companiesRes.json();
      const list = Array.isArray(json) ? json : (json.data || []);
      for (const c of list) {
        const companyId: string = c.id || c.companyId;
        const company: import('@/types').Company = {
          id: companyId,
          name: c.name || c.companyName || '',
          publicName: c.publicName || c.name || '',
          slug: c.slug || '',
          description: c.description,
          logoUrl: c.branding?.logoUrl || c.logoUrl,
          // v3.4 (25/05/26) — needed by ProfilePage Aziende tab to render
          // "Elimina" (owner) vs "Lascia" (member) CTA per company.
          ownerUid: c.ownerUid,
        };
        // Normalize permission — BE sends 'admin'|'write'|'read'
        const rawPerm = c.permission || c.role;
        const permission: import('@/types').Permission =
          rawPerm === 'admin' ? 'admin' :
          rawPerm === 'write' || rawPerm === 'recruiter' ? 'write' :
          'read';
        memberships[companyId] = {
          companyId,
          permission,
          jobRole: c.jobRole ?? null,
          company,
        };
      }
    }
  } catch {
    // Non-fatal — new user has no companies yet
  }

  const preferredLanguage =
    sessionJson.preferredLanguage === 'en' || sessionJson.preferredLanguage === 'it'
      ? sessionJson.preferredLanguage
      : undefined;

  // Phase 2 Agency: read the canonical user profile so we pick up
  // `accountType`, `agencyPlanId`, `agencyBillingPeriod`, and the composed
  // `agency` dashboard payload (only present when accountType === 'agency_owner').
  // Without this the SubscriptionTab branch on `user.accountType === 'agency_owner'`
  // would never fire and the user would keep seeing the SME pricing grid even
  // after a successful Stripe checkout for an Agency plan. Optional: any
  // failure here is non-fatal so signup of brand-new users (where the profile
  // doc may take a tick to materialise) still succeeds.
  let agencyExtras: {
    accountType?: 'individual' | 'agency_owner' | 'agency_member' | 'invited_member';
    agencyPlanId?: 'agency_starter' | 'agency_professional';
    agencyBillingPeriod?: 'monthly' | 'annual';
    agency?: import('@/types').AgencyContext;
    // v3.4 (25/05/26) — Member confinement: drives FE conditional rendering
    // of the Plan tab, upgrade CTAs, and the readonly plan info card.
    canManagePlan?: boolean;
    effectiveOwner?: {
      uid: string;
      email: string | null;
      displayName: string | null;
    } | null;
    // Sprint 2 Block 2 Fix K (22/05/26 ~18:20): user-level billing plan
    // for individual users. Lets TrialBanner / SubscriptionTab guards
    // skip a round-trip when the SME plan is already known.
    billing?: { planId: string };
  } = {};
  // BE-sourced avatar URL — populated by `PUT /users/me { avatarUrl }`
  // after the recruiter uploads from the Profile page (Plan A Phase 2).
  // Without picking it up here, `refreshSession()` would silently fall
  // back to `firebaseUser.photoURL` and the freshly uploaded avatar
  // would never re-render — the toast would say "Avatar updated" while
  // the avatar pill stayed on the initial fallback.
  let beAvatarUrl: string | undefined;
  let lastSelectedCompanyId: string | undefined;
  try {
    const meRes = await fetch(`${API_BASE}/users/me`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (meRes.ok) {
      const meJson = (await meRes.json()) as {
        avatarUrl?: string;
        accountType?: 'individual' | 'agency_owner' | 'agency_member' | 'invited_member';
        agencyPlanId?: 'agency_starter' | 'agency_professional';
        agencyBillingPeriod?: 'monthly' | 'annual';
        agency?: import('@/types').AgencyContext;
        agencyPlanId?: string;
        hasAgencyWorkspace?: boolean;
        billing?: { planId?: string };
        canManagePlan?: boolean;
        lastSelectedCompanyId?: string;
        effectiveOwner?: {
          uid: string;
          email: string | null;
          displayName: string | null;
        } | null;
        // Some callers wrap as { data: { ... } }; handle both.
        data?: Record<string, unknown>;
      };
      const payload = (meJson.data && typeof meJson.data === 'object'
        ? meJson.data
        : meJson) as typeof meJson;
      beAvatarUrl = typeof payload.avatarUrl === 'string' ? payload.avatarUrl : undefined;
      agencyExtras = {
        ...(payload.accountType ? { accountType: payload.accountType } : {}),
        ...(payload.agencyPlanId ? { agencyPlanId: payload.agencyPlanId } : {}),
        ...(payload.agencyBillingPeriod ? { agencyBillingPeriod: payload.agencyBillingPeriod } : {}),
        ...(payload.agency ? { agency: payload.agency } : {}),
        ...(payload.hasAgencyWorkspace ? { hasAgencyWorkspace: true } : {}),
        ...(payload.billing?.planId ? { billing: { planId: payload.billing.planId } } : {}),
        // v3.4 — default canManagePlan to true if BE hasn't been deployed yet
        // (older BE responses won't include the field).
        canManagePlan: payload.canManagePlan ?? true,
        ...(payload.effectiveOwner !== undefined
          ? { effectiveOwner: payload.effectiveOwner }
          : {}),
      };
      lastSelectedCompanyId =
        typeof payload.lastSelectedCompanyId === 'string' && payload.lastSelectedCompanyId.trim()
          ? payload.lastSelectedCompanyId.trim()
          : undefined;
    }
  } catch {
    // Non-fatal — profile may not exist yet for fresh signups
  }

  return {
    id: firebaseUser.uid,
    email: firebaseUser.email || '',
    displayName:
      typeof sessionJson.displayName === 'string' && sessionJson.displayName.trim()
        ? sessionJson.displayName
        : firebaseUser.displayName || firebaseUser.email || '',
    // Precedence: BE doc (recruiter-uploaded via Profile) → Firebase
    // photoURL (Google profile picture) → undefined.
    avatarUrl: beAvatarUrl || firebaseUser.photoURL || undefined,
    timeZone: typeof sessionJson.timeZone === 'string' ? sessionJson.timeZone : undefined,
    preferredLanguage,
    lastSelectedCompanyId,
    memberships,
    ...agencyExtras,
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedCompany, setSelectedCompanyRaw] = useState<Company | null>(null);
  const enrichingRef = useRef<string | null>(null);

  // Enrich a company object with full data from API if name is missing
  const enrichAndSetCompany = useCallback(async (company: Company) => {
    setSelectedCompanyRaw(company);
    void api.auth.updateProfile({ lastSelectedCompanyId: company.id }).catch(() => {
      // Non-fatal — sidebar still switches; reload may fall back until next save.
    });
    // If company already has a name, no need to fetch
    if (company.name && company.name.trim()) return;
    // Avoid duplicate fetches
    if (enrichingRef.current === company.id) return;
    enrichingRef.current = company.id;
    try {
      const full = await api.companies.get(company.id);
      if (full) {
        setSelectedCompanyRaw(prev => prev?.id === company.id ? { ...prev, ...full } : prev);
      }
    } catch {
      // Non-fatal
    } finally {
      if (enrichingRef.current === company.id) enrichingRef.current = null;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        setFirebaseUser(fbUser);
        try {
          const u = await syncBackendSession(fbUser);
          setUser(u);
          const initialCompany = pickInitialCompany(u);
          if (initialCompany) {
            void enrichAndSetCompany(initialCompany);
          } else {
            setSelectedCompanyRaw(null);
          }
        } catch {
          setUser({
            id: fbUser.uid,
            email: fbUser.email || '',
            displayName: fbUser.displayName || fbUser.email || '',
            avatarUrl: fbUser.photoURL || undefined,
            memberships: {},
          });
        }
      } else {
        setFirebaseUser(null);
        setUser(null);
        setSelectedCompanyRaw(null);
      }
      setIsLoading(false);
    });

    return unsubscribe;
  }, [enrichAndSetCompany]);

  // Sentry context propagation: every event captured during this session will
  // automatically include the user id/email and the currently-selected company
  // id + plan, making it easy to filter errors per-customer in the Sentry UI.
  useEffect(() => {
    if (user) {
      setSentryUser({
        id: user.id,
        email: user.email,
        username: user.displayName,
      });
    } else {
      setSentryUser(null);
    }
  }, [user]);

  useEffect(() => {
    setSentryTag('company_id', selectedCompany?.id ?? 'none');
    setSentryTag('company_name', selectedCompany?.name ?? 'none');
  }, [selectedCompany]);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      setFirebaseUser(cred.user);
      const u = await syncBackendSession(cred.user);
      setUser(u);
      const initialCompany = pickInitialCompany(u);
      if (initialCompany) void enrichAndSetCompany(initialCompany);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signup = useCallback(async (name: string, email: string, password: string): Promise<void> => {
    setIsLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: name });
      await cred.user.getIdToken(true);
      setFirebaseUser(cred.user);
      const u = await syncBackendSession(cred.user);
      setUser(u);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loginWithGoogle = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const cred = await signInWithPopup(auth, googleProvider);

      // Check if user already exists in Firestore
      const userDoc = await getDoc(doc(db, 'users', cred.user.uid));
      const isNewUser = !userDoc.exists();

      setFirebaseUser(cred.user);
      const u = await syncBackendSession(cred.user);
      setUser(u);

      if (isNewUser) {
        // New user: account created, now redirect to onboarding
        const err = new Error('NEW_GOOGLE_USER') as Error & { code: string };
        err.code = 'NEW_GOOGLE_USER';
        throw err;
      }

      const initialCompany = pickInitialCompany(u);
      if (initialCompany) void enrichAndSetCompany(initialCompany);
    } finally {
      setIsLoading(false);
    }
  }, [enrichAndSetCompany]);

  const logout = useCallback(async (): Promise<void> => {
    await signOut(auth);
    setUser(null);
    setFirebaseUser(null);
    setSelectedCompanyRaw(null);
    localStorage.removeItem('app-language');
  }, []);

  const refreshSession = useCallback(async (): Promise<void> => {
    const fb = firebaseUser;
    if (!fb) return;
    try {
      const u = await syncBackendSession(fb);
      setUser(u);
    } catch {
      // Non-fatal — profile may still be stale until next navigation
    }
  }, [firebaseUser]);

  const authProviders = {
    google: firebaseUser?.providerData.some((p) => p.providerId === 'google.com') ?? false,
    password: firebaseUser?.providerData.some((p) => p.providerId === 'password') ?? false,
  };
  const isGoogleOnly = !!firebaseUser && authProviders.google && !authProviders.password;

  const value: AuthContextType = {
    user,
    firebaseUser,
    isAuthenticated: !!user,
    isLoading,
    selectedCompany,
    authProviders,
    isGoogleOnly,
    login,
    signup,
    loginWithGoogle,
    logout,
    setSelectedCompany: enrichAndSetCompany,
    refreshSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
